import React, { useCallback, useMemo } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import { QuickBonus, Slider } from '@atlas/components';
import { AccountManager } from '@atlas/core';
import { Icon, Label, ToggleCheckbox } from '@atlas/ui';

import { BASE_PATH, QUICK_DEPOSIT_POPUP_TITLE_TYPE } from 'src/common/constants';
import { CDN_TYPE_CRM, imageUrlCDN } from 'src/common/utils';
import useLanguage from 'src/hooks/useLanguage';

import { useQuickDepositBonusContext } from './QuickDepositBonusContext';

const TITLE = {
  [QUICK_DEPOSIT_POPUP_TITLE_TYPE.QUICK_DEPOSIT]: 'quick_deposit_get_bonus',
  [QUICK_DEPOSIT_POPUP_TITLE_TYPE.INSUFFICIENT]: 'insufficient_funds_get_bonus',
};
const FALLBACK_BONUS_IMAGE = `${BASE_PATH}/static/assets/images/gift.svg`;

const getBonusFinance = (bonus, currency) =>
  bonus?.financeDataList?.find(item => item.currency === currency) || bonus?.financeData;

const getBonusImageLink = (bonus, alpha3Code) => {
  const bonusImage = bonus?.localizedImageInfoList?.find(
    item => item.language?.toLowerCase() === alpha3Code?.toLowerCase(),
  );

  return bonusImage?.imageLink
    ? imageUrlCDN(bonusImage.imageLink, CDN_TYPE_CRM)
    : FALLBACK_BONUS_IMAGE;
};

const QuickDepositBonuses = ({
  currencyFormatter,
  isRtl = false,
  titleType = QUICK_DEPOSIT_POPUP_TITLE_TYPE.QUICK_DEPOSIT,
}) => {
  const { alpha3Code } = useLanguage();
  const currency = AccountManager.getAccountCurrency();
  const {
    activatedBonus,
    amountToGetBonus,
    availableBonuses,
    bonusesList,
    isBonusesDisabled,
    selectedBonus,
    setSelectedBonus,
    shouldRenderInSlider,
    showBonus,
  } = useQuickDepositBonusContext();

  const bonusList = useMemo(
    () => bonusesList ?? (activatedBonus ? [activatedBonus] : (availableBonuses ?? [])),
    [activatedBonus, availableBonuses, bonusesList],
  );
  const sliderKey = useMemo(() => bonusList.map(({ id }) => id).join('-'), [bonusList]);
  const dir = isRtl ? 'rtl' : 'ltr';
  const hasSingleBonus = bonusList.length === 1;
  const isSingleBonusWithoutSlider = hasSingleBonus && !shouldRenderInSlider;
  const titleMessage = TITLE[titleType] ?? TITLE[QUICK_DEPOSIT_POPUP_TITLE_TYPE.INSUFFICIENT];

  const handleSelectBonus = useCallback(
    bonus => {
      if (activatedBonus?.id === bonus.id) {
        setSelectedBonus(bonus);
        return;
      }

      const isActive = selectedBonus?.id === bonus.id;
      const selected = isActive ? null : bonus;
      setSelectedBonus(selected);
    },
    [activatedBonus, selectedBonus, setSelectedBonus],
  );

  const sliderConfig = useMemo(
    () => ({
      autoHeight: false,
      autoplay: false,
      className: 'deposit-bonus__slider',
      dir,
      loop: false,
      nextBtn: <Slider.NextBtn className="deposit-bonus-slider__next-arrow" />,
      prevBtn: <Slider.PrevBtn className="deposit-bonus-slider__prev-arrow" />,
      slidesPerView: 'auto',
      spaceBetween: 8,
    }),
    [dir],
  );

  if (!bonusList.length || !showBonus) {
    return null;
  }

  const renderBonus = bonus => {
    const finance = getBonusFinance(bonus, currency);
    const imageLink = getBonusImageLink(bonus, alpha3Code);
    const isActive = selectedBonus?.id === bonus.id || activatedBonus?.id === bonus.id;

    const bonusTitle = <h4 className="quick-deposit-bonus-title">{bonus?.name}</h4>;

    const bonusDescription = (
      <div className="quick-deposit-bonus-description">
        <Label
          message="deposit_min_amount"
          params={{
            currency: currency || bonus?.currency,
            minBonusAmount: finance?.minDeposit,
          }}
        />
      </div>
    );

    const disableBlock = (
      <div className="quick-deposit-bonus-disable">
        <Icon className="disabled-icon" name="lock" />
        <Label className="disabled-title" message="deposit_bonus_unavaliable_message" />
      </div>
    );

    return (
      <div
        className={classNames('quick-deposit-bonus', {
          'quick-deposit-bonus--single': hasSingleBonus,
        })}
        key={bonus.id}
      >
        <QuickBonus
          active={isActive}
          description={bonusDescription}
          disabled={bonus.disabled || false}
          disabledBlock={disableBlock}
          image={imageLink}
          title={bonusTitle}
          toggleCheckbox={
            <ToggleCheckbox
              checked={isActive}
              disabled={isBonusesDisabled || bonus?.disabled || false}
              onChange={() => handleSelectBonus(bonus)}
            />
          }
        />
      </div>
    );
  };

  return (
    <>
      {amountToGetBonus > 0 && isBonusesDisabled && (
        <Label
          className="quick-deposit-bonus-error"
          message="quick_deposit_add_amount_to_get_bonus"
          params={{
            amount: currencyFormatter.format(amountToGetBonus),
          }}
        />
      )}

      <div
        className={classNames('quick-deposit-wrapper', {
          single: hasSingleBonus,
        })}
      >
        {!hasSingleBonus && <Label className="slider-title" message={titleMessage} />}
        {isSingleBonusWithoutSlider ? (
          renderBonus(bonusList[0])
        ) : (
          <div
            className={classNames('slider-wrapper', {
              single: hasSingleBonus,
            })}
          >
            <Slider key={sliderKey} {...sliderConfig}>
              {bonusList.map(bonus => (
                <Slider.Slide className="quick-deposit-slide" key={bonus.id}>
                  {renderBonus(bonus)}
                </Slider.Slide>
              ))}
            </Slider>
          </div>
        )}
      </div>
    </>
  );
};

QuickDepositBonuses.propTypes = {
  currencyFormatter: PropTypes.shape({
    format: PropTypes.func.isRequired,
  }).isRequired,
  isRtl: PropTypes.bool,
  titleType: PropTypes.string,
};

export default QuickDepositBonuses;
